import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";

function initials(value = "") {
  return String(value).split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "WP";
}

export default function OwnerClientLinkDialog({ open, client, context, onClose, onLinked }) {
  const [query, setQuery] = React.useState("");
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [savingId, setSavingId] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setQuery(client?.email || client?.name || "");
  }, [open, client]);

  React.useEffect(() => {
    if (!open) return undefined;
    const timeout = setTimeout(async () => {
      setLoading(true); setError("");
      try {
        const response = await ownerApi.searchWebProfitUsers({ query, limit: 20 });
        setUsers(response?.users || []);
      } catch (err) {
        setError(formatApiError(err, "No pude buscar usuarios WebProfit."));
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timeout);
  }, [open, query]);

  const link = async (userId) => {
    setSavingId(userId || "unlink"); setError("");
    try {
      const response = await ownerApi.linkClientWebProfitUser(client.id || client._id, userId);
      onLinked?.(response?.context || null, response?.client || null);
      onClose?.();
    } catch (err) { setError(formatApiError(err, "No pude vincular la cuenta.")); }
    finally { setSavingId(""); }
  };

  return (
    <Dialog open={open} onClose={savingId ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography sx={{ fontWeight: 1000 }}>Cuenta WebProfit</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: ".8rem", fontWeight: 700 }}>Vinculá la persona Owner con su usuario global. Sus membresías sólo sugieren proyectos; el vínculo comercial con cada Project se confirma por separado.</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.2} sx={{ pt: .7 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Buscar usuario"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nombre o email"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
          {context?.user ? <Alert severity="info">Actualmente vinculada a {context.user.name} · {context.user.email}</Alert> : null}
          {loading ? <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} /></Box> : (
            <Stack spacing={.7}>
              {users.map((user) => (
                <Button key={user.id} onClick={() => link(user.id)} disabled={Boolean(savingId)} sx={{ p: 1.1, justifyContent: "flex-start", textTransform: "none", borderRadius: 2.2, border: "1px solid", borderColor: "divider" }}>
                  <Avatar sx={{ width: 34, height: 34, mr: 1.1, fontSize: ".75rem", fontWeight: 1000 }}>{initials(user.name)}</Avatar>
                  <Box sx={{ minWidth: 0, textAlign: "left", flex: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 950, fontSize: ".86rem" }}>{user.name}</Typography>
                    <Typography noWrap sx={{ color: "text.secondary", fontSize: ".72rem" }}>{user.email} · {user.projectCount} proyecto{user.projectCount === 1 ? "" : "s"}</Typography>
                  </Box>
                  {savingId === user.id ? <CircularProgress size={18} /> : <LinkRoundedIcon fontSize="small" />}
                </Button>
              ))}
              {!users.length && !loading ? <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary", fontSize: ".82rem" }}>No encontré usuarios con esa búsqueda.</Typography> : null}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        {context?.user ? <Button color="error" onClick={() => link(null)} disabled={Boolean(savingId)} sx={{ mr: "auto", textTransform: "none", fontWeight: 850 }}>Desvincular</Button> : null}
        <Button onClick={onClose} disabled={Boolean(savingId)} sx={{ textTransform: "none", fontWeight: 850 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
